import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/base/FilmCard";
import FilmCardSkeleton from "@/components/base/FilmCardSkeleton";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { films, sortOptions } from "@/mocks/films";
import { filterFilms } from "@/lib/film";
import { useDelayedLoading } from "@/hooks/useDemoLoading";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterPanel from "./components/FilterPanel";
import MobileFilterSheet from "./components/MobileFilterSheet";

const PAGE_SIZE = 12;

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);

  const genres = useMemo(
    () => (params.get("genre") ?? "").split(",").filter(Boolean),
    [params],
  );
  const tag = params.get("tag") ?? "";
  const year = params.get("year") ?? "全部";
  const region = params.get("region") ?? "全部";
  const sort = params.get("sort") ?? "latest";
  const pageParam = Number(params.get("page") ?? "1") || 1;

  const filtered = useMemo(() => {
    const base = filterFilms(films, { genres, year, region, sort });
    return tag ? base.filter((film) => film.tags.includes(tag)) : base;
  }, [genres, year, region, sort, tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(Math.max(1, pageParam), totalPages);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeCount = genres.length + (year !== "全部" ? 1 : 0) + (region !== "全部" ? 1 : 0) + (tag ? 1 : 0);
  const loadingKey = `${genres.join("-")}|${tag}|${year}|${region}|${sort}`;
  const loading = useDelayedLoading(loadingKey, 240);

  const heading = tag ? `标签：${tag}` : genres.length > 0 ? `${genres.join(" / ")}影片` : "分类浏览";

  usePageMeta({
    title: `${heading} - 日本一级片`,
    description: "浏览日本一级影片分类，筛选查找符合兴趣的电影。",
    keywords: "日本一级片分类,日本影片筛选,日本电影类型,日本影视资源",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "全部") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.delete("page");
    setParams(next, { replace: true });
  };

  const toggleGenre = (genre: string) => {
    const next = genres.includes(genre) ? genres.filter((item) => item !== genre) : [...genres, genre];
    updateParam("genre", next.join(","));
  };

  const resetAll = () => setParams(new URLSearchParams(), { replace: true });

  const handlePageChange = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(nextPage));
    }
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteLayout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "分类浏览" }]} />
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{heading}</h1>
              <p className="mt-2 text-sm text-foreground-600 max-w-[560px]">
                按类型、年份与地区组合筛选，快速定位你想看的日本影片资料。
              </p>
            </div>
            <p className="text-sm text-foreground-500 font-label shrink-0">共 {filtered.length} 部影片</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="lg:flex lg:items-start lg:gap-6">
          <aside className="hidden lg:block w-[268px] shrink-0 sticky top-[88px]">
            <FilterPanel
              genres={genres}
              year={year}
              region={region}
              onToggleGenre={toggleGenre}
              onYearChange={(value) => updateParam("year", value)}
              onRegionChange={(value) => updateParam("region", value)}
              onReset={resetAll}
              activeCount={activeCount}
            />
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-4 md:mb-5">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateParam("sort", option.value)}
                    className={`h-8 px-3 rounded-md text-xs md:text-[13px] whitespace-nowrap transition-colors cursor-pointer border ${
                      sort === option.value
                        ? "bg-secondary-800 border-secondary-800 text-background-50"
                        : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="lg:hidden h-8 px-3 inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 text-xs text-foreground-800 whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className="ri-equalizer-line w-4 h-4 flex items-center justify-center"></i>
                筛选{activeCount > 0 ? `（${activeCount}）` : ""}
              </button>
            </div>

            {tag && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">当前标签</span>
                <button
                  type="button"
                  onClick={() => updateParam("tag", "")}
                  className="h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md bg-accent-100 text-accent-900 text-xs cursor-pointer"
                >
                  {tag}
                  <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {Array.from({ length: 6 }).map((_, index) => (
                  <FilmCardSkeleton key={index} />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <EmptyState
                title="无符合条件的影片"
                description="试试减少筛选条件，或重置后重新浏览全部影片。"
                actionLabel="重置筛选"
                onAction={resetAll}
                secondaryLabel="返回首页"
                secondaryPath="/"
              />
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                  {visible.map((film) => (
                    <FilmCard key={film.id} film={film} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
              </>
            )}
          </div>
        </div>
      </div>

      <MobileFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        genres={genres}
        year={year}
        region={region}
        onToggleGenre={toggleGenre}
        onYearChange={(value) => updateParam("year", value)}
        onRegionChange={(value) => updateParam("region", value)}
        onReset={resetAll}
        activeCount={activeCount}
        resultCount={filtered.length}
      />
    </SiteLayout>
  );
}